import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Box,
    Avatar,
    Menu,
    MenuItem,
    Divider,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Close as CloseIcon,
    Logout,
    Settings,
    Search,
    DarkMode,
    LightMode,
    CorporateFare,
} from '@mui/icons-material';
import { useAuth } from '@store/authStore';
import { NotificationBell, NotificationPanel } from '@/components/Notifications';
import { AppLauncher } from '@/components/AppLauncher';
import useNotificationStore from '@/store/NotificationStore';
import { useNotificationPolling } from '@/hooks/useNotificationPolling';
// Logo will be loaded from public folder

interface HeaderProps {
    onMenuClick: () => void;
    isSidebarOpen?: boolean;
    isSidebarExpanded?: boolean;
    darkMode?: boolean;
    onDarkModeChange?: () => void;
    onSearchClick?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onMenuClick, isSidebarOpen = false, isSidebarExpanded = false, darkMode = false, onDarkModeChange, onSearchClick }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Mobile and tablets (to match sidebar behavior)
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // Tablets
    const isDesktop = useMediaQuery(theme.breakpoints.up('md')); // Desktop and up
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    // Notification state
    const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
    const { unreadCount, panelOpen, setPanelOpen } = useNotificationStore();

    // Initialize notification polling
    useNotificationPolling({ interval: 60000, enabled: true });

    // Get user from auth store or localStorage - simplified
    const userData = user || (() => {
        try {
            const userJson = localStorage.getItem('user');
            return userJson ? JSON.parse(userJson) : null;
        } catch {
            return null;
        }
    })();

    // Debug: Log user data changes
    useEffect(() => {
        console.log('🎭 Header: User data changed:', {
            hasUser: !!userData,
            profilePhoto: userData?.profilePhoto,
            name: userData?.name
        });
        console.log('🎭 Header: Full userData object:', userData);

        // Test if photo URL is accessible
        if (userData?.profilePhoto) {
            const img = new Image();
            img.onload = () => console.log('✅ Header: Photo URL is accessible:', userData.profilePhoto);
            img.onerror = () => console.log('❌ Header: Photo URL is not accessible:', userData.profilePhoto);
            img.src = userData.profilePhoto;
        }
    }, [userData?.profilePhoto, userData?.name]);


    // Listen for auth store changes only
    useEffect(() => {
        if (user) {
            console.log('🔄 Header: Auth store user changed:', user);
        }
    }, [user]);

    const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        console.log('Logout function:', typeof logout);
        if (typeof logout === 'function') {
            logout();
            handleClose();
            navigate('/login');
        } else {
            console.error('Logout function is not available');
        }
    };

    return (
        <AppBar
            position="fixed"
            sx={{
                zIndex: 1400, // Higher than sidebar (1200) but not too high
                background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: theme.palette.mode === 'dark'
                    ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                    : '0 8px 32px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease'
            }}
        >
            <Toolbar sx={{
                py: 1,
                px: { xs: 1, sm: 2 },
                minHeight: { xs: 56, sm: 64 },
                justifyContent: 'space-between',
                position: 'relative'
            }}>
                {/* Hamburger Menu Button */}
                <IconButton
                    color="inherit"
                    aria-label={
                        isMobile
                            ? (isSidebarOpen ? "close drawer" : "open drawer")
                            : (isSidebarExpanded ? "collapse sidebar" : "expand sidebar")
                    }
                    onClick={onMenuClick}
                    edge="start"
                    sx={{
                        mr: { xs: 1, sm: 2 },
                        color: theme.palette.text.primary,
                        width: { xs: 40, sm: 44, md: 48 },
                        height: { xs: 40, sm: 44, md: 48 },
                        minWidth: { xs: 40, sm: 44, md: 48 },
                        '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                        },
                        '& svg': {
                            color: theme.palette.text.primary,
                            fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' }
                        }
                    }}
                >
                    {isMobile
                        ? (isSidebarOpen ? <CloseIcon /> : <MenuIcon />)
                        : (isSidebarExpanded ? <CloseIcon /> : <MenuIcon />)
                    }
                </IconButton>

                {/* Logo and Title Section */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexGrow: 1,
                    minWidth: 0,
                    overflow: 'hidden'
                }}>
                    <Box display="flex" alignItems="center" gap={{ xs: 0.5, sm: 1 }} sx={{ flexShrink: 0 }}>
                        <Box sx={{
                            width: { xs: 32, sm: 40, md: 48 },
                            height: { xs: 32, sm: 40, md: 48 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: { xs: 0.5, sm: 1 }
                        }}>
                            <img
                                src="/assets/logos/logo-192.png"
                                alt="Nucleus Logo"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain'
                                }}
                            />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{
                                color: theme.palette.text.primary,
                                fontWeight: 700,
                                fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' },
                                lineHeight: 1.2,
                                mb: 0.25
                            }}>
                                Nucleus
                            </Typography>
                            <Typography variant="body2" sx={{
                                color: theme.palette.text.secondary,
                                fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.8rem' },
                                lineHeight: 1.2,
                                display: { xs: 'none', sm: 'block' }
                            }}>
                                Welcome {userData?.name || 'Admin User'}! 👋
                            </Typography>
                        </Box>
                    </Box>

                    {/* Mobile Search Button - Opens CommandBar */}
                    <Box sx={{
                        display: { xs: 'flex', md: 'none' },
                        flexGrow: 1,
                        mx: { xs: 1, sm: 2 },
                        maxWidth: { xs: '200px', sm: '250px' }
                    }}>
                        <Box
                            onClick={onSearchClick}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                width: '100%',
                                backgroundColor: theme.palette.action.hover,
                                borderRadius: 2,
                                px: 1.5,
                                py: 0.75,
                                cursor: 'pointer',
                                border: `1px solid ${theme.palette.divider}`,
                                '&:hover': {
                                    borderColor: theme.palette.text.primary,
                                    backgroundColor: theme.palette.action.selected
                                }
                            }}
                        >
                            <Search sx={{ color: theme.palette.text.secondary, fontSize: { xs: '1rem', sm: '1.1rem' } }} />
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                                Search...
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Search Trigger - Desktop Only, Opens CommandBar */}
                <Box sx={{
                    display: { xs: 'none', md: 'flex' }, // Hide on mobile/tablet
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: {
                        md: '350px',
                        lg: '450px',
                        xl: '500px'
                    },
                    zIndex: 1
                }}>
                    <Box
                        onClick={onSearchClick}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            backgroundColor: theme.palette.action.hover,
                            borderRadius: 2,
                            px: 2,
                            py: 1,
                            cursor: 'pointer',
                            border: `1px solid ${theme.palette.divider}`,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                borderColor: theme.palette.text.primary,
                                backgroundColor: theme.palette.action.selected
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Search sx={{ color: theme.palette.text.secondary }} />
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                Search commands, pages...
                            </Typography>
                        </Box>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                            borderRadius: 1,
                            px: 1,
                            py: 0.25
                        }}>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                                Ctrl+K
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Right Side Icons */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 0.25, sm: 1, md: 2 },
                    minWidth: 0,
                    flexShrink: 0,
                    ml: { xs: 0.5, sm: 1 }
                }}>

                    {/* Dark Mode Toggle - Desktop Only */}
                    <Box
                        onClick={() => onDarkModeChange?.()}
                        sx={{
                            display: { xs: 'none', md: 'flex' }, // Hide on mobile and tablet
                            position: 'relative',
                            width: 70,
                            height: 32,
                            backgroundColor: darkMode ? '#000000' : '#ff9800',
                            borderRadius: '20px',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            alignItems: 'center',
                            padding: '3px',
                            '&:hover': {
                                transform: 'scale(1.05)',
                                boxShadow: darkMode
                                    ? '0 4px 12px rgba(0, 0, 0, 0.3)'
                                    : '0 4px 12px rgba(255, 152, 0, 0.3)',
                            }
                        }}
                    >
                        {/* Icon on the left */}
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '8px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '20px',
                                height: '20px',
                            }}
                        >
                            {darkMode ? (
                                <DarkMode sx={{
                                    color: 'white',
                                    fontSize: '16px',
                                    filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))',
                                }} />
                            ) : (
                                <LightMode sx={{
                                    color: 'white',
                                    fontSize: '16px',
                                    filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))',
                                }} />
                            )}
                        </Box>

                        {/* Circular knob */}
                        <Box
                            sx={{
                                width: 26,
                                height: 26,
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                position: 'absolute',
                                right: '3px',
                                top: '50%',
                                transform: darkMode ? 'translateY(-50%) translateX(0)' : 'translateY(-50%) translateX(-36px)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 2,
                            }}
                        >
                            {/* Icon inside the knob */}
                            {darkMode ? (
                                <LightMode sx={{
                                    color: '#ff9800',
                                    fontSize: '14px',
                                }} />
                            ) : (
                                <DarkMode sx={{
                                    color: '#000000',
                                    fontSize: '14px',
                                }} />
                            )}
                        </Box>
                    </Box>

                    {/* Notification Bell with Badge */}
                    <NotificationBell
                        unreadCount={unreadCount}
                        onClick={(event) => {
                            setNotificationAnchorEl(event.currentTarget);
                            setPanelOpen(true);
                        }}
                    />

                    {/* Notification Panel Dropdown */}
                    <NotificationPanel
                        anchorEl={notificationAnchorEl}
                        open={panelOpen}
                        onClose={() => {
                            setNotificationAnchorEl(null);
                            setPanelOpen(false);
                        }}
                    />

                    {/* App Launcher */}
                    <AppLauncher />

                    {/* Profile Avatar */}
                    <IconButton
                        size="small"
                        aria-label="account of current user"
                        aria-controls="menu-appbar"
                        aria-haspopup="true"
                        onClick={handleProfileClick}
                        color="inherit"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: { xs: 36, sm: 44, md: 48 },
                            height: { xs: 36, sm: 44, md: 48 },
                            minWidth: { xs: 36, sm: 44, md: 48 },
                            p: 0
                        }}
                    >
                        <Avatar
                            key={`avatar-${userData?.id}-${userData?.profilePhoto || 'no-photo'}`}
                            src={userData?.profilePhoto}
                            sx={{
                                width: { xs: 32, sm: 40, md: 44 },
                                height: { xs: 32, sm: 40, md: 44 },
                                background: userData?.profilePhoto ? 'transparent' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                border: '2px solid rgba(255, 255, 255, 0.2)',
                                fontSize: { xs: '0.8rem', sm: '1rem', md: '1.1rem' }
                            }}
                        >
                            {!userData?.profilePhoto && (userData?.name?.charAt(0) || 'A')}
                        </Avatar>
                    </IconButton>

                    <Menu
                        id="menu-appbar"
                        anchorEl={anchorEl}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        keepMounted
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                        PaperProps={{
                            sx: {
                                backgroundColor: darkMode
                                    ? 'rgba(26, 31, 58, 0.95)'
                                    : 'rgba(248, 249, 250, 0.95)',
                                backdropFilter: 'blur(10px)',
                                border: darkMode
                                    ? '1px solid rgba(255, 255, 255, 0.1)'
                                    : '1px solid rgba(0, 0, 0, 0.1)',
                                borderRadius: 2,
                                mt: 1,
                                minWidth: 200,
                            }
                        }}
                    >
                        {/* Organization */}
                        <MenuItem onClick={() => {
                            handleClose();
                            navigate('/organization');
                        }} sx={{
                            color: theme.palette.text.primary
                        }}>
                            <CorporateFare sx={{ mr: 1 }} />
                            Organization
                        </MenuItem>
                        {/* Settings - HR goes to /settings, others go to /profile/settings */}
                        <MenuItem onClick={() => {
                            handleClose();
                            navigate(userData?.role === 'HR' ? '/settings' : '/profile/settings');
                        }} sx={{
                            color: theme.palette.text.primary
                        }}>
                            <Settings sx={{ mr: 1 }} />
                            {userData?.role === 'HR' ? 'Settings' : 'Profile Settings'}
                        </MenuItem>
                        <Divider sx={{
                            backgroundColor: theme.palette.divider
                        }} />
                        <MenuItem onClick={handleLogout} sx={{
                            color: theme.palette.text.primary
                        }}>
                            <Logout sx={{ mr: 1 }} />
                            Logout
                        </MenuItem>
                    </Menu>
                </Box>
            </Toolbar>
        </AppBar>
    );
};
